/**
 * VotingScreen component
 *
 * Displays an image and vote buttons (Yes/No/Skip)
 * Keyboard shortcuts: Y = Yes, N = No, S = Skip
 */

import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ThumbsUp, ThumbsDown, SkipForward } from 'lucide-react';
import { Button } from '../ui/Button';
import type { CurrentImage } from '../../hooks/useVoteFlow';

interface VotingScreenProps {
  image: CurrentImage;
  imageCount: number;
  onVote: (liked: boolean) => void;
  onSkip: () => void;
}

export function VotingScreen({
  image,
  imageCount,
  onVote,
  onSkip,
}: VotingScreenProps) {
  const { t, i18n } = useTranslation();
  const isJapanese = i18n.language === 'ja';

  // Keyboard shortcuts for voting
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'y':
          event.preventDefault();
          onVote(true);
          break;
        case 'n':
          event.preventDefault();
          onVote(false);
          break;
        case 's':
          event.preventDefault();
          onSkip();
          break;
      }
    },
    [onVote, onSkip]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="flex flex-col items-center">
      {/* Image counter and skip button */}
      <div className="mb-4 flex w-full max-w-2xl items-center justify-between px-4">
        <span className="text-sm text-gray-600 dark:text-gray-200">
          {t('voting.image_counter', { current: imageCount })}
        </span>
        <Button
          variant="skip"
          size="sm"
          onClick={onSkip}
          aria-label={t('accessibility.skip_image_label')}
        >
          <span className="hidden sm:inline">{t('voting.skip')}</span>
          <SkipForward className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      {/* Image container */}
      <div className="w-full max-w-2xl overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-600 dark:bg-black">
        <div className="relative aspect-[4/3] w-full bg-gray-100 dark:bg-gray-900">
          <img
            src={image.cdn_url}
            alt={isJapanese && image.title_ja ? image.title_ja : image.title_en}
            className="absolute inset-0 h-full w-full object-contain"
            loading="eager"
          />
        </div>
      </div>

      {/* Vote buttons */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:gap-4">
        <Button
          variant="vote-yes"
          size="lg"
          onClick={() => onVote(true)}
          aria-label={t('accessibility.vote_yes_label')}
          className="min-w-[180px]"
        >
          <ThumbsUp className="h-5 w-5" aria-hidden="true" />
          {t('voting.yes_like')}
        </Button>

        <Button
          variant="vote-no"
          size="lg"
          onClick={() => onVote(false)}
          aria-label={t('accessibility.vote_no_label')}
          className="min-w-[180px]"
        >
          <ThumbsDown className="h-5 w-5" aria-hidden="true" />
          {t('voting.no_dislike')}
        </Button>
      </div>

      {/* Keyboard hint - hidden on mobile */}
      <p className="mt-3 hidden text-xs text-gray-400 sm:block" aria-hidden="true">
        {t('voting.keyboard_hint')}
      </p>

      {/* Categories (optional display) */}
      {image.categories.length > 0 && (
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {image.categories.map((category) => (
            <span
              key={category.id}
              className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-900 dark:text-gray-200"
            >
              {isJapanese && category.display_name_ja
                ? category.display_name_ja
                : category.display_name_en}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
